/// <reference types="office-js" />

import { showToast } from "./toast";

// ============================================
// UTILITAIRES / NORMALISATION
// ============================================

function normalizeForWordMatching(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .replace(/\r\n|\r|\n/g, "\n")
    .replace(/\s+/g, " ")
    .replace(/[“”]/g, '"')
    .replace(/[’]/g, "'")
    .trim();
}

type ReferencePayload = {
  text: string; // target text (may be empty)
  update: string; // replacement (may be empty)
  before?: string;
  after?: string;
};

function scoreParagraph(pText: string, { text, before, after }: ReferencePayload): number {
  const P = normalizeForWordMatching(pText);
  const T = text ? normalizeForWordMatching(text) : null;
  const B = before ? normalizeForWordMatching(before) : null;
  const A = after ? normalizeForWordMatching(after) : null;

  let score = 0;

  if (B && P.includes(B)) score += 3;
  if (T && P.includes(T)) score += 5;
  if (A && P.includes(A)) score += 3;

  // Order sensitivity
  if (B && T && P.indexOf(B) < P.indexOf(T)) score += 1;
  if (T && A && P.indexOf(T) < P.indexOf(A)) score += 1;

  return score;
}

/**
 * Calculate dynamic threshold based on available fields in payload.
 *
 * Rules:
 * - If `text` is provided: require text match (5 points minimum)
 * - If `text` is empty but both `before` and `after` provided: require both contexts (6 points)
 * - If `text` is empty and only one context: require that context (3 points)
 * - If `text` is empty and no context: invalid case, threshold = 0 (will fail)
 */
function calculateThreshold(payload: ReferencePayload): number {
  const hasText = !!(payload.text && payload.text.length > 0);

  if (hasText) {
    // Text is the most critical field - must find it
    return 5;
  }

  return 3;
}

function findBestParagraph(
  paragraphs: Word.ParagraphCollection,
  payload: ReferencePayload
): { paragraph: Word.Paragraph | null; score: number; threshold: number; bestIndex: number } {
  let best: Word.Paragraph | null = null;
  let bestScore = 0;
  let bestIndex = -1;

  // Calculate dynamic threshold based on available fields
  const threshold = calculateThreshold(payload);

  for (let i = 0; i < paragraphs.items.length; i++) {
    const p = paragraphs.items[i];
    const score = scoreParagraph(p.text, payload);
    if (score > bestScore) {
      bestScore = score;
      best = p;
      bestIndex = i;
    }
  }

  // Note: We return the best paragraph even if below threshold
  // This allows multi-paragraph fallback to use it
  return { paragraph: best, score: bestScore, threshold, bestIndex };
}

function extractRangeInParagraph(paragraph: Word.Paragraph, text: string): Word.Range | null {
  const normalizedText = normalizeForWordMatching(text);
  if (!normalizedText) {
    return null;
  }

  const ranges = paragraph.search(normalizedText, {
    ignoreSpace: true,
    ignorePunct: true,
    matchCase: false,
    matchWholeWord: false,
  });

  const first = ranges.getFirstOrNullObject();
  paragraph.context.load(first, "isNullObject");
  return first;
}

// For deletion or generation (text === ""), paragraph-level replacement is acceptable.
function extractContextRange(paragraph: Word.Paragraph): Word.Range {
  return paragraph.getRange();
}

/**
 * Try to find text by combining the best paragraph with adjacent paragraphs.
 * This handles cases where text spans multiple paragraphs.
 */
async function tryMultiParagraphMatch(
  context: Word.RequestContext,
  paragraphs: Word.ParagraphCollection,
  bestIndex: number,
  payload: ReferencePayload,
  threshold: number
): Promise<Word.Range | null> {
  const MAX_COMBINATIONS = 3; // Try up to 3 consecutive paragraphs

  // Try expanding forward (best + next paragraphs)
  for (
    let count = 2;
    count <= MAX_COMBINATIONS && bestIndex + count - 1 < paragraphs.items.length;
    count++
  ) {
    const startPara = paragraphs.items[bestIndex];
    const endPara = paragraphs.items[bestIndex + count - 1];

    // Create combined range spanning multiple paragraphs
    const startRange = startPara.getRange("Start");
    const endRange = endPara.getRange("End");
    const combinedRange = startRange.expandTo(endRange);

    // Load and get text from combined range
    context.load(combinedRange, "text");
    await context.sync();

    // Score the combined text
    const combinedScore = scoreParagraph(combinedRange.text, payload);

    if (combinedScore >= threshold) {
      // Combined range meets threshold, try to find exact text within it
      if (payload.text && payload.text.length > 0) {
        // First check: exact match of normalized combined text (for whole-paragraph replacements)
        const normalizedCombinedText = normalizeForWordMatching(combinedRange.text);
        const normalizedPayloadText = normalizeForWordMatching(payload.text);

        if (normalizedCombinedText === normalizedPayloadText) {
          // Exact match - return entire combined range
          try {
            console.log(`[WordMatch] Exact multi-paragraph match: ${count} paragraphs`);
          } catch {
            // ignore console issues
          }
          return combinedRange;
        }

        // Second check: search for text within combined range
        const normalizedText = normalizeForWordMatching(payload.text);
        const searchResults = combinedRange.search(normalizedText, {
          ignoreSpace: true,
          ignorePunct: true,
          matchCase: false,
          matchWholeWord: false,
        });

        context.load(searchResults, "items");
        await context.sync();

        if (searchResults.items.length > 0) {
          // Found text in combined range
          try {
            console.log(
              `[WordMatch] Multi-paragraph match: ${count} paragraphs, score ${combinedScore}, threshold ${threshold}`
            );
          } catch {
            // ignore console issues
          }
          return searchResults.items[0];
        }
      } else {
        // No text to search, return the combined range
        try {
          console.log(
            `[WordMatch] Multi-paragraph match: ${count} paragraphs, score ${combinedScore}, threshold ${threshold}`
          );
        } catch {
          // ignore console issues
        }
        return combinedRange;
      }
    }
  }

  // Try expanding backward (previous paragraphs + best)
  for (let count = 2; count <= MAX_COMBINATIONS && bestIndex - count + 1 >= 0; count++) {
    const startPara = paragraphs.items[bestIndex - count + 1];
    const endPara = paragraphs.items[bestIndex];

    const startRange = startPara.getRange("Start");
    const endRange = endPara.getRange("End");
    const combinedRange = startRange.expandTo(endRange);

    context.load(combinedRange, "text");
    await context.sync();

    const combinedScore = scoreParagraph(combinedRange.text, payload);

    if (combinedScore >= threshold) {
      if (payload.text && payload.text.length > 0) {
        // First check: exact match of normalized combined text
        const normalizedCombinedText = normalizeForWordMatching(combinedRange.text);
        const normalizedPayloadText = normalizeForWordMatching(payload.text);

        if (normalizedCombinedText === normalizedPayloadText) {
          try {
            console.log(`[WordMatch] Exact multi-paragraph match (backward): ${count} paragraphs`);
          } catch {
            // ignore console issues
          }
          return combinedRange;
        }

        // Second check: search for text within combined range
        const normalizedText = normalizeForWordMatching(payload.text);
        const searchResults = combinedRange.search(normalizedText, {
          ignoreSpace: true,
          ignorePunct: true,
          matchCase: false,
          matchWholeWord: false,
        });

        context.load(searchResults, "items");
        await context.sync();

        if (searchResults.items.length > 0) {
          try {
            console.log(
              `[WordMatch] Multi-paragraph match (backward): ${count} paragraphs, score ${combinedScore}, threshold ${threshold}`
            );
          } catch {
            // ignore console issues
          }
          return searchResults.items[0];
        }
      } else {
        try {
          console.log(
            `[WordMatch] Multi-paragraph match (backward): ${count} paragraphs, score ${combinedScore}, threshold ${threshold}`
          );
        } catch {
          // ignore console issues
        }
        return combinedRange;
      }
    }
  }

  return null;
}

async function findBestRangeForPayload(
  context: Word.RequestContext,
  payload: ReferencePayload
): Promise<Word.Range | null> {
  const body = context.document.body;
  const paragraphs = body.paragraphs;
  context.load(paragraphs, "items,text");
  await context.sync();

  if (!paragraphs.items || paragraphs.items.length === 0) {
    return null;
  }

  const {
    paragraph: bestParagraph,
    score,
    threshold,
    bestIndex,
  } = findBestParagraph(paragraphs, payload);

  // Audit logging
  try {
    console.log("[WordMatch] bestScore", score, "threshold", threshold, "for payload", {
      text: payload.text ? `${payload.text.substring(0, 50)}...` : "(empty)",
      before: payload.before ? `${payload.before.substring(0, 30)}...` : "(empty)",
      after: payload.after ? `${payload.after.substring(0, 30)}...` : "(empty)",
    });
  } catch {
    // ignore console issues in Office runtime
  }

  if (!bestParagraph) {
    return null;
  }

  // Strategy 1: Try single-paragraph match if score meets threshold
  if (score >= threshold) {
    if (payload.text && payload.text.length > 0) {
      // First, try exact paragraph match (for whole-paragraph replacements)
      const normalizedParagraphText = normalizeForWordMatching(bestParagraph.text);
      const normalizedPayloadText = normalizeForWordMatching(payload.text);

      if (normalizedParagraphText === normalizedPayloadText) {
        // Exact match - return entire paragraph range
        try {
          console.log("[WordMatch] Exact paragraph match found");
        } catch {
          // ignore console issues
        }
        return extractContextRange(bestParagraph);
      }

      // Otherwise, try to find text within the paragraph
      const candidate = extractRangeInParagraph(bestParagraph, payload.text);
      context.load(candidate, "isNullObject");
      await context.sync();

      if (candidate && !candidate.isNullObject) {
        return candidate;
      }
      // If text not found in single paragraph, fall through to multi-paragraph
    } else {
      return extractContextRange(bestParagraph);
    }
  }

  // Strategy 2: Multi-paragraph fallback (when single-paragraph fails or text spans paragraphs)
  if (payload.text && payload.text.length > 0 && bestIndex >= 0) {
    // If best score is very low (0 or 1), the "best" paragraph is arbitrary
    // Try multi-paragraph from multiple starting points
    const startingIndices =
      score <= 1 && paragraphs.items.length > 1
        ? [
            bestIndex,
            Math.max(0, bestIndex - 1),
            Math.min(paragraphs.items.length - 1, bestIndex + 1),
          ].filter((idx, pos, arr) => arr.indexOf(idx) === pos) // Remove duplicates
        : [bestIndex];

    for (const startIdx of startingIndices) {
      const multiParaRange = await tryMultiParagraphMatch(
        context,
        paragraphs,
        startIdx,
        payload,
        threshold
      );
      if (multiParaRange) {
        return multiParaRange;
      }
    }
  }

  // Explicit failure: no reliable match found
  return null;
}

async function applyEdit(
  context: Word.RequestContext,
  range: Word.Range | null,
  update: string,
  track: boolean
): Promise<void> {
  const doc = context.document;

  // Preserve existing tracking mode
  const initialMode = doc.changeTrackingMode;
  context.load(doc, "changeTrackingMode");
  await context.sync();

  if (track && initialMode !== Word.ChangeTrackingMode.trackAll) {
    doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    await context.sync();
  }

  if (range) {
    if (update === "") {
      range.delete();
    } else {
      range.insertText(update, Word.InsertLocation.replace);
    }
  } else if (update !== "") {
    // Explicit fallback: append to end of document
    doc.body.insertText(update, Word.InsertLocation.end);
  }

  await context.sync();

  if (track && initialMode !== Word.ChangeTrackingMode.trackAll) {
    doc.changeTrackingMode = initialMode;
    await context.sync();
  }
}

export async function getDocumentAsFile(): Promise<File | null> {
  return new Promise((resolve) => {
    Office.context.document.getFileAsync(Office.FileType.Compressed, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        console.error("Error getting file:", result.error.message);
        resolve(null);
        return;
      }

      const file = result.value;
      const slices: Uint8Array[] = [];
      let sliceIndex = 0;

      const getSlice = () => {
        file.getSliceAsync(sliceIndex, (sliceResult) => {
          if (sliceResult.status === Office.AsyncResultStatus.Succeeded) {
            slices.push(new Uint8Array(sliceResult.value.data));
            sliceIndex++;

            if (sliceIndex < file.sliceCount) {
              getSlice();
            } else {
              const blob = new Blob(slices as unknown as BlobPart[], {
                type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              });
              const docFile = new File([blob], "document.docx", {
                type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              });
              file.closeAsync();
              resolve(docFile);
            }
          } else {
            console.error("Error getting slice:", sliceResult.error.message);
            file.closeAsync();
            resolve(null);
          }
        });
      };

      getSlice();
    });
  });
}

export async function convertBlobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      } else {
        reject(new Error("Failed to convert blob to base64"));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function getDocumentText(): Promise<string> {
  return Word.run(async (context) => {
    try {
      let body = context.document.body;
      body.load("text");
      await context.sync();
      return body.text || "";
    } catch (error) {
      console.error("Error getting document text:", error);
      return "";
    }
  });
}

export async function getSelectionText(): Promise<string> {
  return Word.run(async (context) => {
    try {
      const selection = context.document.getSelection();
      selection.load("text,isEmpty");
      await context.sync();

      if (selection.isEmpty) {
        return "";
      }

      return selection.text || "";
    } catch (error) {
      console.error("Error getting selection text:", error);
      return "";
    }
  });
}

export async function getDocumentTextAndSelection(): Promise<[text: string, selection: string]> {
  let selectionContent = "";
  let bodyContent = "";

  return Word.run(async (context) => {
    // Get selection text
    try {
      const selection = context.document.getSelection();
      selection.load("text,isEmpty");
      await context.sync();
      if (!selection.isEmpty) {
        selectionContent = selection.text;
      }
    } catch (error) {
      console.error("Error getting selection:", error);
    }

    // Get document text
    try {
      const body = context.document.body;
      body.load("text");
      await context.sync();
      bodyContent = body.text || "";
    } catch (error) {
      console.error("Error getting body text:", error);
    }

    return [bodyContent, selectionContent];
  });
}

export async function replace(searchValue: string, replaceValue: string): Promise<void> {
  try {
    if (!searchValue) {
      return;
    }

    await Word.run(async (context) => {
      const payload: ReferencePayload = {
        text: searchValue,
        update: replaceValue,
      };
      const range = await findBestRangeForPayload(context, payload);

      if (!range) {
        showToast("Impossible de trouver un emplacement fiable pour le remplacement.", "error");
        return;
      }

      await applyEdit(context, range, replaceValue, false);
    });
  } catch (error) {
    showToast("Impossible de remplacer le texte : " + JSON.stringify(error), "error");
  }
}

export async function replaceRefWith(
  context: Word.RequestContext,
  ref: Word.Range,
  text: string,
  options?: { select?: boolean }
): Promise<void> {
  const { select = false } = options || {};

  try {
    if (select) {
      ref.select();
      await context.sync();
    }

    ref.insertText(text, Word.InsertLocation.replace);

    await context.sync();
  } catch (error) {
    showToast("Impossible de remplacer la phrase : " + JSON.stringify(error), "error");
  }
}

export async function selectText(text: string): Promise<void> {
  if (!text) {
    return;
  }

  await Word.run(async (context) => {
    const range = await findTextInWord(context, text);
    if (range) {
      range.select();
      await context.sync();
    }
  });
}

export async function selectTextInsideSentence(sentence: string, text: string): Promise<void> {
  if (!sentence) {
    return;
  }

  await Word.run(async (context) => {
    try {
      const sentenceRef = context.document.body
        .search(sentence, {
          matchCase: false,
          matchWholeWord: false,
          ignoreSpace: true,
          ignorePunct: true,
        })
        .getFirstOrNullObject();

      context.load(sentenceRef, "isNullObject");
      await context.sync();

      if (sentenceRef.isNullObject) {
        await selectText(text);
        return;
      }

      const textRef = sentenceRef.search(text).getFirstOrNullObject();
      context.load(textRef, "isNullObject");
      await context.sync();

      if (textRef.isNullObject) {
        sentenceRef.select();
        await context.sync();
        return;
      }

      textRef.select();
      await context.sync();
    } catch (e) {
      await selectText(text);
    }
  });
}

export async function selectSentence(
  quote: string,
  error: string | undefined,
  correction: string | undefined,
  quoteWithCorrection: string
): Promise<void> {
  if (!quote) {
    return;
  }

  await Word.run(async (context) => {
    const sentence = await findSentence(
      context,
      { quote, error, correction, quoteWithCorrection },
      { includeCorrected: true }
    );
    showToast(JSON.stringify(sentence), "info");
    if (sentence) {
      sentence.select();
      return;
    }
  });
}

export async function findSentence(
  context: Word.RequestContext,
  typo: {
    quote: string;
    error: string | undefined;
    correction: string | undefined;
    quoteWithCorrection: string;
  },
  options?: {
    includeCorrected?: boolean;
  }
): Promise<Word.Range | undefined> {
  const { quote, error, correction, quoteWithCorrection } = typo;
  const { includeCorrected = false } = options || {};

  if (!quote) {
    return undefined;
  }

  const body = context.document.body;

  const quoteResults = body.search(quote);
  const quoteRef = quoteResults.getFirstOrNullObject();
  context.load(quoteRef, "isNullObject");
  await context.sync();

  // 1. Search for the original quote
  if (!quoteRef.isNullObject) {
    // 1.1 Search for correction inside original quote
    if (includeCorrected && correction && correction.length > 0) {
      const correctionResultsInQuote = quoteRef.search(correction);
      const correctionRefInQuote = correctionResultsInQuote.getFirstOrNullObject();
      context.load(correctionRefInQuote, "isNullObject");
      await context.sync();
      if (!correctionRefInQuote.isNullObject) {
        return correctionRefInQuote;
      }
    }

    // 1.2 Search for error inside original quote
    if (error && error.length > 0) {
      const errorResultsInQuote = quoteRef.search(error);
      const errorRefInQuote = errorResultsInQuote.getFirstOrNullObject();
      context.load(errorRefInQuote, "isNullObject");
      await context.sync();
      if (!errorRefInQuote.isNullObject) {
        return errorRefInQuote;
      }
    }

    // Fallback: select the whole original sentence
    return quoteRef;
  }

  const quoteWithCorrectionResults = body.search(quoteWithCorrection);
  const quoteWithCorrectionRef = quoteWithCorrectionResults.getFirstOrNullObject();
  context.load(quoteWithCorrectionRef, "isNullObject");
  await context.sync();

  // 2. Search for original quote with diff
  if (includeCorrected && !quoteWithCorrectionRef.isNullObject) {
    // 2.1 Search for original quote with both error and correction
    const errorWithCorrectionResults = quoteWithCorrectionRef.search(correction + error);
    const errorWithCorrectionRef = errorWithCorrectionResults.getFirstOrNullObject();
    context.load(errorWithCorrectionRef, "isNullObject");
    await context.sync();
    if (!errorWithCorrectionRef.isNullObject) {
      return errorWithCorrectionRef;
    }

    // 2.2 Search for correction inside original quote with diff
    if (correction && correction.length > 0) {
      const correctionResultsInQuote = quoteWithCorrectionRef.search(correction);
      const correctionRefInQuote = correctionResultsInQuote.getFirstOrNullObject();
      context.load(correctionRefInQuote, "isNullObject");
      await context.sync();
      if (!correctionRefInQuote.isNullObject) {
        return correctionRefInQuote;
      }
    }

    // 2.3 Search for error inside original quote with diff
    if (error && error.length > 0) {
      const errorResultsInQuote = quoteWithCorrectionRef.search(error);
      const errorRefInQuote = errorResultsInQuote.getFirstOrNullObject();
      context.load(errorRefInQuote, "isNullObject");
      await context.sync();
      if (!errorRefInQuote.isNullObject) {
        return errorRefInQuote;
      }
    }

    return quoteWithCorrectionRef;
  }

  // 3. Search for the correction
  if (includeCorrected && correction && correction.length > 0) {
    const correctionResults = body.search(correction);
    const correctionRef = correctionResults.getFirstOrNullObject();
    context.load(correctionRef, "isNullObject");
    await context.sync();
    if (!correctionRef.isNullObject) {
      return correctionRef;
    }
  }

  // 4. Search for the error
  if (error && error.length > 0) {
    const errorResults = body.search(error);
    const errorRef = errorResults.getFirstOrNullObject();
    context.load(errorRef, "isNullObject");
    await context.sync();
    if (!errorRef.isNullObject) {
      return errorRef;
    }
  }

  return undefined;
}

export async function findTextInWord(
  context: Word.RequestContext,
  text: string,
  before?: string,
  after?: string
): Promise<Word.Range | undefined> {
  const payload: ReferencePayload = {
    text,
    update: "",
    before,
    after,
  };

  const range = await findBestRangeForPayload(context, payload);
  return range || undefined;
}
